// Progressive-enhancement wrapper around Shopify's native Search & Discovery
// collection.filters. Every filter pill is a real <a href> built server-side
// from url_to_add/url_to_remove — this component only intercepts clicks to
// avoid a full page reload; with JS disabled the links still work.
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    // Split as in the cart drawer: listeners on this element and on window
    // survive the innerHTML swap after a filter click, so binding them per
    // render would stack a duplicate each time and fire one click as many
    // fetches as there had been renders.
    if (!this.hasSetUp) {
      this.hasSetUp = true;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-filter-link]');
        if (!link) return;
        event.preventDefault();
        this.fetchAndRender(link.href);
      });

      window.addEventListener('popstate', () => this.fetchAndRender(window.location.href, false));
    }

    this.bindPriceInputs();
  }

  // The price inputs really are replaced on every render, so they do need
  // rebinding each time.
  bindPriceInputs() {
    this.querySelectorAll('[data-filter-price]').forEach((input) => {
      input.addEventListener(
        'change',
        this.debounce(() => this.applyPriceFilter(), 400)
      );
    });
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  applyPriceFilter() {
    const url = new URL(window.location.href);
    this.querySelectorAll('[data-filter-price]').forEach((input) => {
      if (!input.dataset.param) return;
      if (input.value) {
        url.searchParams.set(input.dataset.param, input.value);
      } else {
        url.searchParams.delete(input.dataset.param);
      }
    });
    this.fetchAndRender(url.toString());
  }

  async fetchAndRender(url, pushState = true) {
    this.setAttribute('aria-busy', 'true');
    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', 'main-collection-product-grid');
      const response = await fetch(fetchUrl.toString());
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newContent = doc.querySelector('collection-filters');
      if (!newContent) return;

      this.innerHTML = newContent.innerHTML;
      this.bindPriceInputs();

      if (pushState) {
        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', cleanUrl.toString());
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
